import {
    TrndsClientTiktokAudio,
    TrndsClientTiktokAudioStats,
    getTrndsClientTiktokAudioById,
    getTrndsClientTiktokAudioStatsById,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../../../helpers';

export function useClientTiktokAudioModalData({
    clientId,
    audioId,
}: {
    clientId: number;
    audioId: number | undefined;
}) {
    const [tiktokAudio, setTiktokAudio] = useState<TrndsClientTiktokAudio | null>(null);
    const [tiktokAudioStats, setTiktokAudioStats] = useState<TrndsClientTiktokAudioStats | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchData = useCallback(
        async (requestInit: RequestInit) => {
            if (!audioId) {
                return;
            }
            try {
                setIsLoading(true);
                setHasError(false);
                const [audioResponse, audioStatsResponse] = await Promise.allSettled([
                    getTrndsClientTiktokAudioById({ clientId, audioId }, requestInit),
                    getTrndsClientTiktokAudioStatsById({ clientId, audioId }, requestInit),
                ]);

                if (audioResponse.status === 'fulfilled' && audioResponse.value.status === 200) {
                    setTiktokAudio(audioResponse.value.data);
                }

                if (audioStatsResponse.status === 'fulfilled' && audioStatsResponse.value.status === 200) {
                    setTiktokAudioStats(audioStatsResponse.value.data);
                }
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                showNotification(`couldn't fetch audio data`, 'error');
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, audioId]
    );

    const init = useCallback(
        (signal: AbortSignal) => {
            fetchData({ signal });
        },
        [fetchData]
    );

    const reset = useCallback(() => {
        setTiktokAudio(null);
        setTiktokAudioStats(null);
    }, []);

    return { tiktokAudio, tiktokAudioStats, isInitialized, isLoading, hasError, init, reset };
}
